/**
 * useOfflineStatus Hook
 *
 * Provides offline/online status, read-only mode indicator, and pending queue count.
 */

import { useState, useEffect } from 'react';
import { offlineCacheService } from '../services/offline-cache.service';
import { offlineQueueService } from '../services/offline-queue.service';
import { CACHE_KEYS } from '../config/cache-config';

export interface OfflineStatus {
  isOnline: boolean;
  isReadOnly: boolean;
  pendingQueueCount: number;
  isSyncing: boolean;
  lastSyncAt: number | null;
  cacheStats: {
    totalKeys: number;
    totalSize: number;
    storageQuotaUsed: number;
  };
}

export function useOfflineStatus(): OfflineStatus {
  const [status, setStatus] = useState<OfflineStatus>(() => getStatus());

  useEffect(() => {
    const handleOnline = () => setStatus(getStatus());
    const handleOffline = () => setStatus(getStatus());

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const unsub = offlineQueueService.subscribe(() => {
      setStatus(getStatus());
    });

    const interval = setInterval(() => {
      setStatus(getStatus());
    }, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsub();
      clearInterval(interval);
    };
  }, []);

  return status;
}

function getStatus(): OfflineStatus {
  const queueStatus = offlineQueueService.getStatus();
  const cacheStats = offlineCacheService.getStats();
  const readOnly = offlineCacheService.get<boolean>(CACHE_KEYS.READ_ONLY_MODE) === true;

  return {
    isOnline: queueStatus.isOnline,
    isReadOnly: readOnly,
    pendingQueueCount: queueStatus.pendingCount,
    isSyncing: queueStatus.isSyncing,
    lastSyncAt: queueStatus.lastSyncAt,
    cacheStats: {
      totalKeys: cacheStats.totalKeys,
      totalSize: cacheStats.totalSize,
      storageQuotaUsed: cacheStats.storageQuotaUsed,
    },
  };
}
